/**
 * Level 1 aggregate row — score · records-by-band · detriment mix.
 * The column order here is the contract for the whole report (SPEC §5.3).
 */

import { BAND_COLORS, bandOf } from '../constants';
import type { AggregateBlock, DetrimentType } from '../types';
import { fmt } from '../utils';
import { BandGauge, BandHistogram, Delta, DetrimentMixBar } from './parts';

const CELL = 'min-w-0';
const LABEL =
  'mb-1.5 text-[9px] font-extrabold uppercase tracking-[0.1em] text-muted-foreground';

export function AggregateRow({
  aggregate,
  detrimentDrilldownHref,
}: {
  aggregate: AggregateBlock;
  /** Links each detriment figure into the records queue for that dimension. */
  detrimentDrilldownHref?: (type: DetrimentType) => string;
}) {
  const band = bandOf(aggregate.score);
  // Scale the histogram against the aggregate's OWN largest band — not the
  // per-card shared max, whose values are far smaller than the dataset totals,
  // which made the moderate bar overflow its box.
  const aggMax = Math.max(1, aggregate.bands.low, aggregate.bands.mod, aggregate.bands.high);
  return (
    <div className="mb-6 grid grid-cols-1 items-center gap-6 rounded-[var(--radius)] border border-border bg-background px-5 py-4 md:grid-cols-[0.85fr_1.35fr_1.6fr]">
      {/* Conduct score */}
      <div className={CELL}>
        <div className={LABEL}>Conduct score</div>
        <div className="flex items-baseline gap-1">
          <span
            className="text-[2rem] font-extrabold leading-none tracking-[-0.035em]"
            style={{ color: BAND_COLORS[band] }}
          >
            {aggregate.score.toFixed(1)}
          </span>
          <Delta cur={aggregate.score} prev={aggregate.scorePrev} dp={1} />
        </div>
        <BandGauge score={aggregate.score} />
        <div className="mt-1.5 text-[10px] font-medium text-muted-foreground">
          {aggregate.coveragePct.toFixed(1)}% of records carry an outcome
        </div>
      </div>

      {/* Records by band */}
      <div className={`${CELL} border-l border-border pl-6`}>
        <div className={LABEL}>Records by band</div>
        <div className="flex items-baseline gap-2">
          <span className="text-[2rem] font-extrabold leading-none tracking-[-0.035em]">
            {fmt(aggregate.volume)}
          </span>
          <Delta cur={aggregate.volume} prev={aggregate.volumePrev} pct neutral />
        </div>
        <div className="mt-2">
          <BandHistogram bands={aggregate.bands} max={aggMax} height={52} />
        </div>
      </div>

      {/* Detriment mix */}
      <div className={`${CELL} border-l border-border pl-6`}>
        <div className={LABEL}>Detriment mix</div>
        <DetrimentMixBar
          mix={aggregate.detrimentMix}
          keyStyle="full"
          drilldownHref={detrimentDrilldownHref}
        />
        <div className="mt-1.5 text-[10px] font-medium text-muted-foreground">
          Share of records carrying each type — non-exclusive, so one record can carry all three.
        </div>
      </div>
    </div>
  );
}
